import { useEffect } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useSocket } from 'src/providers/socket.provider'
import { useCurrentProject } from 'src/providers/current-project.provider'
import {
  THRCampaign,
  THRCampaignDispatch,
  THRCampaignStatusUpdatedPayload,
  THRSmsLog,
  THRDispatchCreatedPayload,
  THRDispatchUpdatedPayload,
  THRDispatchNewSmsPayload,
  THRDispatchSmsStatusChangePayload,
} from 'src/types/hr'

export const useHRCampaignSocket = (campaignId: number) => {
  const { socket, socketError } = useSocket()
  const queryClient = useQueryClient()
  const { project } = useCurrentProject()

  useEffect(() => {
    if (!socket || socketError) return undefined

    const onDispatchCreated = ({ campaignId: eventCampaignId, dispatch }: THRDispatchCreatedPayload) => {
      if (eventCampaignId !== campaignId) return
      queryClient.setQueryData(
        ['hr-campaign-dispatches', project.id, campaignId],
        (oldDispatches: THRCampaignDispatch[] | undefined) => {
          if (!oldDispatches) return oldDispatches
          if (oldDispatches.some((d) => d.id === dispatch.id)) return oldDispatches
          return [...oldDispatches, dispatch]
        },
      )
    }

    const onDispatchUpdated = ({ campaignId: eventCampaignId, dispatch }: THRDispatchUpdatedPayload) => {
      if (eventCampaignId !== campaignId) return
      queryClient.setQueryData(
        ['hr-campaign-dispatches', project.id, campaignId],
        (oldDispatches: THRCampaignDispatch[] | undefined) => {
          if (!oldDispatches) return oldDispatches
          return oldDispatches.map((d) =>
            d.id === dispatch.id
              ? {
                  ...d,
                  status: dispatch.status,
                  smsStatus: dispatch.smsStatus,
                  delay: dispatch.delay,
                  respondedAt: dispatch.respondedAt,
                }
              : d,
          )
        },
      )
    }

    const onNewSms = ({ campaignId: eventCampaignId, dispatchId, smsLog }: THRDispatchNewSmsPayload) => {
      if (eventCampaignId !== campaignId) return
      queryClient.setQueryData(
        ['hr-sms-logs', project.id, campaignId, dispatchId],
        (oldLogs: THRSmsLog[] | undefined) => {
          if (!oldLogs) return oldLogs
          if (oldLogs.some((l) => l.id === smsLog.id)) return oldLogs
          return [...oldLogs, smsLog]
        },
      )
    }

    const onSmsStatusChange = ({
      campaignId: eventCampaignId,
      dispatchId,
      smsLogId,
      newStatus,
    }: THRDispatchSmsStatusChangePayload) => {
      if (eventCampaignId !== campaignId) return
      queryClient.setQueryData(
        ['hr-sms-logs', project.id, campaignId, dispatchId],
        (oldLogs: THRSmsLog[] | undefined) => {
          if (!oldLogs) return oldLogs
          return oldLogs.map((l) => (l.id === smsLogId ? { ...l, status: newStatus } : l))
        },
      )
    }

    const onCampaignStatusUpdated = ({
      campaignId: eventCampaignId,
      newStatus,
    }: THRCampaignStatusUpdatedPayload) => {
      if (eventCampaignId !== campaignId) return
      queryClient.setQueryData(
        ['hr-campaign', project.id, campaignId],
        (oldCampaign: THRCampaign | undefined) => {
          if (!oldCampaign) return oldCampaign
          return { ...oldCampaign, status: newStatus }
        },
      )
    }

    socket.emit('SUBSCRIBE_CAMPAIGN', { campaignId })
    socket.on('DISPATCH_CREATED', onDispatchCreated)
    socket.on('DISPATCH_UPDATED', onDispatchUpdated)
    socket.on('DISPATCH_NEW_SMS', onNewSms)
    socket.on('DISPATCH_SMS_STATUS_CHANGE', onSmsStatusChange)
    socket.on('CAMPAIGN_STATUS_UPDATED', onCampaignStatusUpdated)

    return () => {
      socket.emit('UNSUBSCRIBE_CAMPAIGN', { campaignId })
      socket.off('DISPATCH_CREATED', onDispatchCreated)
      socket.off('DISPATCH_UPDATED', onDispatchUpdated)
      socket.off('DISPATCH_NEW_SMS', onNewSms)
      socket.off('DISPATCH_SMS_STATUS_CHANGE', onSmsStatusChange)
      socket.off('CAMPAIGN_STATUS_UPDATED', onCampaignStatusUpdated)
    }
  }, [socket, socketError, campaignId, project.id, queryClient])
}
